import type { DataverseChangesetOperation } from './dataverse-changeset'
import type { AccountRoomData } from './rooms-and-systems'

// Pure room-hierarchy logic (2 levels: parent + child). Kept in its own leaf
// module — like scope-tag-buckets.ts — so it stays importable without the
// Dataverse module graph, which reaches env.ts (src/lib/env.ts:6 reads
// window.location at module load and throws outside a browser). Both imports
// above are TYPE-ONLY and erased at build time, which is what keeps that true
// while `buildRoomMoveOperations` still speaks the changeset's own shape.
// Re-exported from rooms-and-systems.ts.
//
// Canonical room order is DFS pre-order: top-level rooms by (sequencenumber,
// room id), each parent immediately followed by its children in the same order.
// Sequence numbers stay globally unique per account — sibling order is just the
// relative order of the siblings' global values.
//
// Depth/cycle spec (must stay equivalent in proposal-api's
// sort_quote_details.py and the CustomQuoteRibbonActions.js webresource): walk
// the parent chain with a seen-set.
//   - no parent                                  -> top-level
//   - parent id absent from the fetched room set -> top-level (orphan / cross-account)
//   - cycle (seen-set hit, chain never ends)     -> top-level
//   - chain ends at a parentless ancestor T      -> child of T
// The last rule clamps: at depth 2 the immediate parent IS T, and any deeper
// chain still ranks under T, ordered among T's children by its own (seq, id).

/** Sort key for rooms within one level: (sequencenumber, room id). */
function compareRoomsWithinLevel(a: AccountRoomData, b: AccountRoomData) {
  const sa = a.dsy_sequencenumber ?? 0
  const sb = b.dsy_sequencenumber ?? 0
  if (sa !== sb) return sa - sb
  return a.dsy_accountroomid < b.dsy_accountroomid ? -1 : a.dsy_accountroomid > b.dsy_accountroomid ? 1 : 0
}

/**
 * Rank parent: the nearest parentless ancestor of `room`, or null when the room
 * ranks as top-level. Returns null for unparented rooms, for cycles, and when
 * the chain hits a parent id outside the fetched set (e.g. a cross-account
 * parent). A chain deeper than the 2-level cap clamps onto its top ancestor.
 */
function resolveRankParentRoomId(room: AccountRoomData, roomsById: Map<string, AccountRoomData>): string | null {
  const parentId = room._dsy_parentroom_value
  if (!parentId) return null
  const seen = new Set<string>([room.dsy_accountroomid])
  let currentId: string | null = parentId
  while (currentId) {
    if (seen.has(currentId)) return null // cycle
    const parent = roomsById.get(currentId)
    if (!parent) return null // unresolvable parent (not in the fetched set)
    seen.add(currentId)
    if (!parent._dsy_parentroom_value) return currentId // parentless ancestor
    currentId = parent._dsy_parentroom_value
  }
  return null
}

/**
 * Room id -> the id it ranks under (null = ranks top-level), per the depth/cycle
 * spec above. This is the *rank* relationship, not the raw `_dsy_parentroom_value`
 * one: for corrupt (depth > 2 / cyclic) data the two diverge, and every surface
 * that walks DFS order emitting sections must key off this map so the sections it
 * emits stay in the order the rows are sorted in.
 */
export function buildRoomRankParentIdMap(rooms: AccountRoomData[]) {
  const roomsById = new Map(rooms.map((room) => [room.dsy_accountroomid, room]))
  const rankParentIdByRoomId = new Map<string, string | null>()
  for (const room of rooms) {
    rankParentIdByRoomId.set(room.dsy_accountroomid, resolveRankParentRoomId(room, roomsById))
  }
  return rankParentIdByRoomId
}

/**
 * Whether a "select all rooms" control reads as all / some / none checked.
 *
 * Child rooms are ordinary entries in `roomIds`, so "all" spans parents and the
 * rooms inside them — their composed `"Parent | Child"` label is what makes that
 * visible.
 */
export function getRoomSelectionState(roomIds: readonly string[], checkedRoomIds: ReadonlySet<string>) {
  const allChecked = roomIds.length > 0 && roomIds.every((id) => checkedRoomIds.has(id))
  return { allChecked, someChecked: !allChecked && roomIds.some((id) => checkedRoomIds.has(id)) }
}

/** Next checked set for a "select all rooms" toggle: all of them, or none. */
export function toggleAllRoomsSelected(roomIds: readonly string[], checkedRoomIds: ReadonlySet<string>) {
  return getRoomSelectionState(roomIds, checkedRoomIds).allChecked ? new Set<string>() : new Set(roomIds)
}

/**
 * DFS pre-order: each top-level room immediately followed by its children.
 *
 * Rooms deeper than the 2-level cap are clamped onto their nearest parentless
 * ancestor. That clamp is display-rank degradation for corrupt data only — it
 * does not re-parent anything. Group membership (getChildRooms /
 * getRoomGroupIds) still reads the raw `_dsy_parentroom_value` relationship, so
 * a clamped room ranks under the ancestor but is not part of its room group.
 */
export function sortRoomsHierarchically(rooms: AccountRoomData[]): AccountRoomData[] {
  const rankParentIdByRoomId = buildRoomRankParentIdMap(rooms)
  const topLevel: AccountRoomData[] = []
  const childrenByParentId = new Map<string, AccountRoomData[]>()
  for (const room of rooms) {
    const parentId = rankParentIdByRoomId.get(room.dsy_accountroomid) ?? null
    if (parentId === null) {
      topLevel.push(room)
    } else {
      const children = childrenByParentId.get(parentId)
      if (children) children.push(room)
      else childrenByParentId.set(parentId, [room])
    }
  }
  topLevel.sort(compareRoomsWithinLevel)
  const result: AccountRoomData[] = []
  for (const parent of topLevel) {
    result.push(parent)
    const children = childrenByParentId.get(parent.dsy_accountroomid)
    if (children) {
      children.sort(compareRoomsWithinLevel)
      result.push(...children)
    }
  }
  return result
}

/**
 * Direct child rooms of `roomId`, in sibling order — the raw
 * `_dsy_parentroom_value` relationship, per the 2-level cap. Empty for leaf and
 * unknown rooms.
 */
export function getChildRooms(roomId: string, rooms: AccountRoomData[]): AccountRoomData[] {
  return rooms.filter((room) => room._dsy_parentroom_value === roomId).sort(compareRoomsWithinLevel)
}

/** The room's own id plus its direct children's ids — the room group selected by a parent. */
export function getRoomGroupIds(roomId: string, rooms: AccountRoomData[]): string[] {
  return [roomId, ...getChildRooms(roomId, rooms).map((room) => room.dsy_accountroomid)]
}

/** Raw parent lookup value, or null when the room has none (or is unknown). */
export function getParentRoomId(roomId: string, rooms: AccountRoomData[]): string | null {
  const room = rooms.find((r) => r.dsy_accountroomid === roomId)
  return room?._dsy_parentroom_value ?? null
}

/**
 * Sibling-scoped reorder plan: the moved sibling group **permutes its own
 * existing sequence numbers**, so global uniqueness holds and no room outside
 * the group is touched. Returns only the rows whose number actually changes.
 *
 * NOT wired to any UI — drag-to-reparent goes through `buildRoomMovePlan`, which
 * handles reorder and re-parent in one plan. This is kept solely as that plan's
 * **equivalence oracle**: a pure same-parent reorder must produce exactly the
 * numbers this simpler, independently-derived algorithm produces
 * (`room-drag-move.test.ts` "a top-level reorder produces exactly the numbers
 * buildSiblingReorderPlan would"). Delete it only together with that test.
 *
 * Sequence numbers stay globally unique per account (Ordering & reordering
 * semantics) — sibling order is just the relative order of the siblings' global
 * values, so re-dealing the group's own values is the whole operation.
 *
 * Fallback: if the group's numbers are not a complete, unique set (a null, or a
 * duplicate from an import), permuting them cannot express the new order — the
 * group is instead appended after `maxSequenceNumber`, which is globally unique
 * by construction.
 */
export function buildSiblingReorderPlan({
  siblings,
  fromIndex,
  toIndex,
  maxSequenceNumber,
}: {
  siblings: AccountRoomData[]
  fromIndex: number
  toIndex: number
  maxSequenceNumber: number
}): { dsy_accountroomid: string; dsy_sequencenumber: number }[] {
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return []
  if (fromIndex >= siblings.length || toIndex >= siblings.length) return []

  const existing = siblings.map((room) => room.dsy_sequencenumber)
  const usable =
    existing.every((seq): seq is number => typeof seq === 'number') && new Set(existing).size === siblings.length
  const slots = usable
    ? [...(existing as number[])].sort((a, b) => a - b)
    : siblings.map((_, index) => maxSequenceNumber + index + 1)

  const reordered = [...siblings]
  reordered.splice(toIndex, 0, ...reordered.splice(fromIndex, 1))

  return reordered
    .map((room, index) => ({ dsy_accountroomid: room.dsy_accountroomid, dsy_sequencenumber: slots[index] }))
    .filter((next, index) => next.dsy_sequencenumber !== reordered[index].dsy_sequencenumber)
}

/**
 * A room's OWN display label: `"{number} - {name}"`, or just the name. Unchanged
 * by nesting — this is the sidebar's row text, where the tree already shows the
 * parent on the row above.
 */
export function formatRoomLabel(room?: AccountRoomData | null) {
  if (!room) return undefined
  if (room.dsy_roomnumber) {
    return `${room.dsy_roomnumber} - ${room.dsy_name}`
  }
  return room.dsy_name
}

/**
 * The composed display label every non-sidebar surface shows:
 * `"{parent's NAME} | {child's full display label}"` — e.g.
 * `"Floor 1 | 101 - Bedroom 1"`. Top-level rooms are unchanged (no parent, no
 * prefix).
 *
 * Two deliberate asymmetries:
 *   - **The parent contributes its name only, never its room number.** Room
 *     numbers are tied to the architectural plan and it is the CHILD's number
 *     that identifies the space; the parent is context.
 *   - **The separator is a pipe**, because both halves can contain `" - "` (the
 *     child's own `"{number} - {name}"`), and a repeated separator makes the
 *     parent/child boundary ambiguous.
 *
 * Depth is capped at 2, so this composes exactly one level.
 */
export function formatComposedRoomLabel(room?: AccountRoomData | null, parentRoom?: AccountRoomData | null) {
  const label = formatRoomLabel(room)
  if (!label) return label
  const parentName = parentRoom?.dsy_name
  if (!parentName) return label
  return `${parentName} | ${label}`
}

// ───────────────────────────────────────────────────────────────────────────
// Sidebar drag-to-move
//
// `removeRoomChildrenOf` and `getRoomChildCount` are adapted from the dnd-kit
// sortable-tree example (github.com/clauderic/dnd-kit,
// `stories/3 - Examples/Tree/utilities.ts`), MIT © 2021 Claudéric Demers.
//
// The drop TARGET, however, is ours and deliberately unlike dnd-kit's example:
// that example derives depth from how far right the pointer drifted, which makes
// nesting an accident of a horizontal wobble. Here the pointer's VERTICAL
// position inside the hovered row decides WHERE the room lands, and horizontal
// travel never contributes to that:
//
//   top 25%    -> BEFORE that row, at that row's own level   (insertion line)
//   bottom 25% -> AFTER that row, at that row's own level    (insertion line)
//   middle 50% -> INSIDE that room, appended last            (ring + tint, NO line)
//
// The two outcomes are mutually exclusive by construction: `insideRoomId` and
// `afterId` are never both meaningful, so a line always means "between" and a
// highlighted room always means "inside".
//
// X is read for exactly one thing — WHETHER there is a target at all
// (`isPointerWithinRoomList`). Vertical-only resolution otherwise maps every Y
// onto some row however far away the pointer is, so a release out in the items
// panel would commit a move from a gesture users read as "abandon the drag".
//
// The projection IS the depth guard: the sidebar is the only place a room can be
// re-parented, and no gesture can project a depth the 2-level cap disallows.
// ───────────────────────────────────────────────────────────────────────────

/** One visible sidebar row, flattened in DFS order. `depth` is 0 or 1. */
export type FlatRoomNode = {
  id: string
  /** **Rank** parent (`buildRoomRankParentIdMap`), never the raw lookup value. */
  parentId: string | null
  depth: number
  /** Rows that RANK under this one — what the drag carries and the caret shows. */
  hasChildren: boolean
  /**
   * Rooms whose raw `_dsy_parentroom_value` is this room — what the depth cap has
   * to be judged on.
   *
   * The two diverge exactly where corrupt depth-3 data does: in `P > C > G` the
   * clamp ranks G under P, so C has NO rank children while still literally
   * holding one. Nesting C anywhere would write a fourth level, which is why
   * `canNestRoomInto` and the projection's depth clamp read this flag and not
   * `hasChildren`. (Rank children imply raw children, never the reverse.)
   */
  hasRawChildren: boolean
  isTemp: boolean
}

/** Which of a hovered row's three bands the gesture is aiming at. */
export type RoomDropZone = 'before' | 'inside' | 'after'

/**
 * Share of the row height each edge band takes. The middle 35% is the nest band.
 *
 * Reorder is the common action and nesting is the rare, consequential one, so
 * the natural aim point — the middle of the row — must NOT be the one that
 * re-parents. On a 32px row the old 25% split left 8px of line, 16px of nest,
 * 8px of line; 32.5% leaves 10.5px of line, 11px of nest, 10.5px of line, so a
 * hand aiming at "between these two rooms" hits a line. The nest band is
 * narrowed further in time, not space, by the dwell below.
 */
export const ROOM_DROP_EDGE_RATIO = 0.325

/**
 * How long the pointer must sit in a row's nest band before the ring arms.
 *
 * A pass-through — the pointer crossing a row on its way somewhere else — must
 * never flash a nest state, and a nest must never be something the hand did by
 * accident. The gesture stays live throughout: before the dwell elapses the
 * nearer EDGE is shown, so the drag always says where a release would land.
 *
 * Pointer only. `ArrowRight` is already an explicit, deliberate nest.
 */
export const ROOM_DROP_INSIDE_DWELL_MS = 150

function isTempRoomId(roomId?: string | null) {
  return !!roomId && roomId.startsWith('temp-')
}

/**
 * The sidebar's flat tree: DFS-ordered rooms with the depth/parent/child facts
 * the drag projection needs. Input must already be in DFS order (the rooms query
 * sorts with `sortRoomsHierarchically`), so this only annotates — it never
 * re-orders, and a corrupt-data row keeps the position the sort gave it.
 */
export function buildFlatRoomNodes(rooms: AccountRoomData[]): FlatRoomNode[] {
  const rankParentIdByRoomId = buildRoomRankParentIdMap(rooms)
  const childCountByParentId = new Map<string, number>()
  for (const parentId of rankParentIdByRoomId.values()) {
    if (parentId) childCountByParentId.set(parentId, (childCountByParentId.get(parentId) ?? 0) + 1)
  }
  const rawParentIds = new Set(rooms.map((room) => room._dsy_parentroom_value).filter((id): id is string => !!id))
  return rooms.map((room) => {
    const parentId = rankParentIdByRoomId.get(room.dsy_accountroomid) ?? null
    return {
      id: room.dsy_accountroomid,
      parentId,
      depth: parentId === null ? 0 : 1,
      hasChildren: (childCountByParentId.get(room.dsy_accountroomid) ?? 0) > 0,
      hasRawChildren: rawParentIds.has(room.dsy_accountroomid),
      isTemp: isTempRoomId(room.dsy_accountroomid),
    }
  })
}

/**
 * Drop the children of every id in `parentIds` — collapsed rooms, and the room
 * being dragged (its children travel with it and need zero writes).
 */
export function removeRoomChildrenOf(nodes: readonly FlatRoomNode[], parentIds: readonly string[]): FlatRoomNode[] {
  const excluded = new Set(parentIds)
  return nodes.filter((node) => {
    if (node.parentId && excluded.has(node.parentId)) {
      if (node.hasChildren) excluded.add(node.id)
      return false
    }
    return true
  })
}

/** Rooms inside `roomId` — the count the drag overlay's "N rooms" badge reports. */
export function getRoomChildCount(nodes: readonly FlatRoomNode[], roomId: string) {
  return nodes.filter((node) => node.parentId === roomId).length
}

/**
 * The collapsed set with the room a drop just landed INSIDE opened.
 *
 * Dropping into a collapsed parent nests correctly and then hides the result:
 * the group stays shut and the room the user just moved is simply gone from the
 * list. Returns the SAME set when nothing has to open, so the caller's state
 * update is a no-op re-render rather than a new identity every drop.
 */
export function expandRoomAfterDrop(
  collapsedRoomIds: ReadonlySet<string>,
  insideRoomId: string | null
): ReadonlySet<string> {
  if (!insideRoomId || !collapsedRoomIds.has(insideRoomId)) return collapsedRoomIds
  const next = new Set(collapsedRoomIds)
  next.delete(insideRoomId)
  return next
}

/**
 * Whether `overId` may take the dragged room INSIDE it. Four rules, all of them
 * hard:
 *   - it is a top-level room (`depth === 0`) — the 2-level cap,
 *   - it is not the room being dragged,
 *   - it is not a `temp-` room (no record yet to bind a parent lookup to),
 *   - the dragged room has no RAW children of its own — again the cap: nesting it
 *     would make its children depth 2. Raw, not rank: a room in the middle of a
 *     hand-written `P > C > G` chain has its grandchild clamped away from it, so
 *     the rank count is 0 while nesting it really would write a fourth level.
 */
export function canNestRoomInto({
  items,
  activeId,
  overId,
}: {
  items: readonly FlatRoomNode[]
  activeId: string
  overId: string
}) {
  if (activeId === overId) return false
  const target = items.find((item) => item.id === overId)
  const active = items.find((item) => item.id === activeId)
  if (!target || !active) return false
  return target.depth === 0 && !target.isTemp && !active.hasRawChildren
}

/**
 * The zone a pointer at `ratio` (0 = row top, 1 = row bottom) is aiming at.
 *
 * The middle band only nests when the hovered room is a legal parent; otherwise
 * it collapses to the NEARER edge, so the middle of an illegal row still
 * reorders rather than becoming a dead zone the drag silently does nothing in.
 */
export function resolveRoomDropZone({
  items,
  activeId,
  overId,
  ratio,
}: {
  items: readonly FlatRoomNode[]
  activeId: string
  overId: string
  ratio: number
}): RoomDropZone {
  if (ratio < ROOM_DROP_EDGE_RATIO) return 'before'
  if (ratio > 1 - ROOM_DROP_EDGE_RATIO) return 'after'
  if (canNestRoomInto({ items, activeId, overId })) return 'inside'
  return ratio < 0.5 ? 'before' : 'after'
}

/**
 * The zone `resolveRoomDropZone` resolved, demoted to the nearer edge until the
 * pointer has DWELLED in the nest band of that same row (`armedInsideId`).
 *
 * The deterministic half of the dwell: the clock lives in the sidebar (a
 * timestamp per hovered row), the decision lives here. Demoting rather than
 * blanking is the point — a gesture in flight always shows where a release
 * would land, it just does not offer to re-parent until the hand has held still.
 */
export function resolveRoomDropZoneWithDwell({
  zone,
  overId,
  armedInsideId,
  ratio,
}: {
  zone: RoomDropZone
  overId: string
  /** The row whose nest band the pointer has already dwelled in; `null` = none. */
  armedInsideId: string | null
  ratio: number
}): RoomDropZone {
  if (zone !== 'inside') return zone
  if (armedInsideId === overId) return 'inside'
  return ratio < 0.5 ? 'before' : 'after'
}

/** Where in a hovered row the pointer sits: 0 = its top edge, 1 = its bottom. */
export function getRoomRowRatio({
  pointerY,
  overRect,
}: {
  pointerY: number
  overRect: { top: number; height: number } | null
}) {
  if (!overRect || overRect.height <= 0) return 0.5
  return (pointerY - overRect.top) / overRect.height
}

/** Viewport-space extent of the room list — a `DOMRect` fits as-is. */
export type RoomListBounds = { left: number; right: number; top: number; bottom: number }

/**
 * How far past the list's edges, horizontally, the pointer may stray and still
 * aim at it: 16px on each side.
 *
 * Sized to the sidebar's own gutter — the list is inset by the nav's 12px
 * padding and 2px border — so the slack covers a pointer riding the sidebar's
 * outer edge and stops there. Anything further is already in the items panel,
 * which is the release this rule exists to cancel.
 */
export const ROOM_DROP_LIST_X_TOLERANCE = 16

/**
 * Whether the pointer is still aimed at the room list.
 *
 * The one thing X decides. Only HORIZONTAL escape counts: a pointer above or
 * below the list is still aimed at it, and resolving to the first/last TOP-LEVEL
 * position is what that gesture means (`resolveRoomListOvershootSlot`).
 *
 * Unmeasurable input — no bounds, or no pointer at all — counts as WITHIN. A
 * failed measurement must not silently turn every drop into a cancel.
 */
export function isPointerWithinRoomList({
  pointerX,
  listBounds,
  tolerance = ROOM_DROP_LIST_X_TOLERANCE,
}: {
  pointerX: number | null
  listBounds: RoomListBounds | null
  tolerance?: number
}) {
  if (pointerX === null || !listBounds) return true
  return pointerX >= listBounds.left - tolerance && pointerX <= listBounds.right + tolerance
}

/**
 * The slot a pointer PAST THE VERTICAL ENDS of the list is aiming at: the first
 * or last position **at the top level**, never "inside whichever group the end
 * row happens to belong to".
 *
 * The live bug this exists for: the collision fallback resolves an off-the-end
 * pointer onto the nearest row, and the edge zones then take that row's OWN
 * depth. When the bottom-most top-level room has children, the last row is a
 * child row — so a stray flick below the list re-parented the dragged room into
 * an unrelated parent, account-wide. A gesture aimed past the end of a list means
 * "the end of the list", and the end of the list is top level.
 *
 * Anchored on the last (first) DEPTH-0 row rather than the last (first) row:
 * `getRoomDropProjection` snaps a depth-0 insertion past a trailing child run, so
 * `after` the last top-level room lands after its children, at the top level.
 */
function resolveRoomListOvershootSlot({
  items,
  activeId,
  pointerY,
  listBounds,
}: {
  items: readonly FlatRoomNode[]
  activeId: string
  pointerY: number
  listBounds: RoomListBounds | null
}): RoomDropSlot | null {
  if (!listBounds) return null
  if (pointerY >= listBounds.top && pointerY <= listBounds.bottom) return null
  const rows = items.filter((item) => item.id !== activeId)
  const anchor =
    pointerY > listBounds.bottom
      ? [...rows].reverse().find((row) => row.depth === 0)
      : rows.find((row) => row.depth === 0)
  if (!anchor) return null
  return { overId: anchor.id, zone: pointerY > listBounds.bottom ? 'after' : 'before' }
}

// ───────────────────────────────────────────────────────────────────────────
// Drag overlay placement
// ───────────────────────────────────────────────────────────────────────────

/**
 * Where the drag card's TOP-LEFT sits relative to the cursor: below and to the
 * right, so the card never covers the row band the cursor is reading — which
 * band that is, is the entire gesture.
 */
export const ROOM_DRAG_CARD_POINTER_OFFSET = { x: 12, y: 18 } as const

/** Card width, and the gap it keeps from the window edges when clamped. */
export const ROOM_DRAG_CARD_WIDTH_PX = 256
export const ROOM_DRAG_CARD_VIEWPORT_MARGIN_PX = 8

/** Keyboard drags park the card below the target row, with this clearance. */
export const ROOM_DRAG_CARD_KEYBOARD_GAP_PX = 6

/**
 * The transform that puts the drag card under the CURSOR.
 *
 * dnd-kit positions the overlay at the dragged row's ORIGINAL rect and moves it
 * by the pointer delta — so a card grabbed by a handle at the row's right edge
 * floats a whole row-width to the LEFT of the cursor and vertically centred on
 * it, covering both the drop line and the name of the room being aimed at. The
 * fix is to stop treating the transform as a translation of the row and treat it
 * as an absolute placement of the card: `activeRect.left + x` IS the card's
 * client left, so `x` is whatever puts that left edge where we want it.
 *
 * Keyboard drags have no pointer — `activatorEvent` is a KeyboardEvent, which is
 * exactly how the two paths are told apart. There the card parks just BELOW the
 * targeted row instead: the coordinate getter aims the card AT that row (it has
 * to, so dnd-kit's own collisions stay in step), which is precisely the row
 * whose line and ring the user is reading. Measured: the getter also centres the
 * card on the row, so nudging by a row height still left the card 97% on top of
 * an armed nest ring — only the target's own bottom edge is a reliable floor.
 */
export function resolveRoomDragOverlayTransform({
  transform,
  activeRect,
  overRect,
  pointer,
  cardWidth = ROOM_DRAG_CARD_WIDTH_PX,
  viewportWidth,
}: {
  transform: { x: number; y: number; scaleX: number; scaleY: number }
  /** The dragged row's rect at drag start — what the overlay is positioned at. */
  activeRect: { left: number; top: number; height: number } | null
  /** The row currently targeted; the keyboard path parks the card under it. */
  overRect: { left: number; bottom: number } | null
  /** Live cursor position, or `null` for a keyboard drag. */
  pointer: { x: number; y: number } | null
  cardWidth?: number
  viewportWidth: number | null
}) {
  if (!activeRect) return transform
  if (!pointer) {
    // No target measured yet (the lift, before the first key): a row lower is
    // still off the row the card was cloned from.
    if (!overRect) return { ...transform, y: transform.y + activeRect.height + ROOM_DRAG_CARD_KEYBOARD_GAP_PX }
    return {
      ...transform,
      x: overRect.left - activeRect.left,
      y: overRect.bottom + ROOM_DRAG_CARD_KEYBOARD_GAP_PX - activeRect.top,
    }
  }

  const margin = ROOM_DRAG_CARD_VIEWPORT_MARGIN_PX
  // A card whose label runs off the window is unreadable exactly when the drag
  // needs reading. Left edge wins a viewport too narrow to hold the whole card.
  const rightLimit = viewportWidth === null ? Infinity : Math.max(margin, viewportWidth - cardWidth - margin)
  const left = Math.min(Math.max(pointer.x + ROOM_DRAG_CARD_POINTER_OFFSET.x, margin), rightLimit)

  return {
    ...transform,
    x: left - activeRect.left,
    y: pointer.y + ROOM_DRAG_CARD_POINTER_OFFSET.y - activeRect.top,
  }
}

/**
 * The slot a POINTER drag is aiming at, or `null` for "nowhere": a pointer that
 * has left the list horizontally, or no hovered row to read a band off.
 *
 * `null` is the cancel: it projects to a `null` projection, which draws no
 * indicator, builds no plan, and is the sentence `describeRoomDropPlacement`
 * announces a landing-less drop with.
 *
 * Order matters. Horizontal escape is checked FIRST, so a pointer that is both
 * below the list and out in the items panel cancels rather than committing at the
 * end of the list: sideways is the abandon gesture, and it outranks the vertical
 * clamp.
 *
 * A keyboard drag has no pointer and never comes through here — it drives the
 * slot model below, which produces the same `(overId, zone)` pairs.
 */
export function resolveRoomDropSlot({
  items,
  activeId,
  overId,
  overRect,
  pointer,
  listBounds,
}: {
  items: readonly FlatRoomNode[]
  activeId: string
  overId: string | null
  overRect: { top: number; height: number } | null
  pointer: { x: number; y: number }
  listBounds: RoomListBounds | null
}): RoomDropSlot | null {
  if (!isPointerWithinRoomList({ pointerX: pointer.x, listBounds })) return null
  const overshoot = resolveRoomListOvershootSlot({ items, activeId, pointerY: pointer.y, listBounds })
  if (overshoot) return overshoot
  if (!overId || !overRect) return null
  const ratio = getRoomRowRatio({ pointerY: pointer.y, overRect })
  return { overId, zone: resolveRoomDropZone({ items, activeId, overId, ratio }) }
}

export type RoomDropProjection = {
  /** The gesture this projection came from — `inside` is the only nesting one. */
  zone: RoomDropZone
  /** The hovered row the zone was read off (pre-snap), for announcements. */
  overId: string
  depth: number
  /** Rank parent the room would land under; `null` = top level. */
  parentId: string | null
  /**
   * The room the drop lands INSIDE; `null` for the edge zones. Mutually
   * exclusive with `afterId` being meaningful — a line means "between", a
   * highlighted room means "inside", and the two are never both on.
   */
  insideRoomId: string | null
  /** Row the insertion line draws after; `null` = above the first row (or `inside`). */
  afterId: string | null
  /** Position among the target's (temp-excluded) siblings. */
  targetIndex: number
}

/**
 * Where the dragged room lands, given the zone the gesture resolved to.
 *
 * `inside` appends to the end of the target's children — including the children
 * a COLLAPSED target is hiding, via `hiddenChildCountByParentId`, so the room
 * does not jump to the head of a list the user cannot see.
 *
 * The edge zones insert at the hovered row's OWN level, with two clamps:
 *   - a room that HAS children can never land at depth 1 (the 2-level cap), and
 *     neither can one aimed at a run inside a `temp-` parent;
 *   - **slot snap**: a top-level insertion adjacent to a run of children lands
 *     AFTER the whole run, because DFS keeps a parent's children contiguous. The
 *     indicator uses the snapped slot too, so the line always draws where the
 *     row will actually appear.
 */
export function getRoomDropProjection({
  items,
  activeId,
  overId,
  zone,
  hiddenChildCountByParentId,
}: {
  items: readonly FlatRoomNode[]
  activeId: string
  overId: string
  zone: RoomDropZone
  hiddenChildCountByParentId?: ReadonlyMap<string, number>
}): RoomDropProjection | null {
  const overIndex = items.findIndex((item) => item.id === overId)
  const activeIndex = items.findIndex((item) => item.id === activeId)
  if (overIndex < 0 || activeIndex < 0) return null

  const activeItem = items[activeIndex]
  const overItem = items[overIndex]

  // Safety net, not the primary guard: `resolveRoomDropZone` already refuses an
  // illegal nest, and the keyboard slot model never proposes one. A caller that
  // hands us one anyway gets the nearest reorder rather than a 3-level write.
  if (zone === 'inside' && canNestRoomInto({ items, activeId, overId })) {
    const visibleChildren = items.filter(
      (item) => item.parentId === overId && !item.isTemp && item.id !== activeId
    ).length
    return {
      zone: 'inside',
      overId,
      depth: 1,
      parentId: overId,
      insideRoomId: overId,
      afterId: null,
      targetIndex: visibleChildren + (hiddenChildCountByParentId?.get(overId) ?? 0),
    }
  }
  const edgeZone: RoomDropZone = zone === 'inside' ? 'after' : zone

  const rows = items.filter((item) => item.id !== activeId)
  // Depth comes from the row being aimed at, clamped by the cap. RAW children
  // clamp it, for the reason `canNestRoomInto` gives: a room whose grandchild the
  // rank clamp hid still cannot go to depth 1. A `temp-` parent's run is off
  // limits for the same reason a `temp-` room is: there is no record to bind the
  // parent lookup to.
  const depth = activeItem.hasRawChildren || isTempRoomId(overItem.parentId) ? 0 : overItem.depth
  const parentId = depth === 0 ? null : overItem.parentId

  // Dragging over itself (the state the drag starts in) means "where it already
  // is" — no zone applies, and the sidebar's no-op check reads this position.
  const overRow = rows.findIndex((item) => item.id === overId)
  let insertAt = overId === activeId ? activeIndex : edgeZone === 'before' ? overRow : overRow + 1
  if (depth === 0) while (rows[insertAt]?.depth === 1) insertAt++

  let siblingsBefore = 0
  for (let index = 0; index < insertAt; index++) {
    const item = rows[index]
    if (item.isTemp) continue
    if (item.parentId === parentId) siblingsBefore++
  }
  const hiddenSiblings = parentId ? (hiddenChildCountByParentId?.get(parentId) ?? 0) : 0

  return {
    zone: edgeZone,
    overId,
    depth,
    parentId,
    insideRoomId: null,
    afterId: rows[insertAt - 1]?.id ?? null,
    targetIndex: siblingsBefore + hiddenSiblings,
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Keyboard slot model
//
// With the pointer's horizontal travel gone, the keyboard needs its own explicit
// nest gesture, so it drives a SLOT LIST rather than synthesised coordinates:
//   ↑/↓  step between adjacent insertion slots
//   →    nest into the room immediately above the slot (when legal)
//   ←    pull the room out to the top level
// Every slot is a `(overId, zone)` pair the pointer could equally produce, so
// both input paths land in exactly the same `getRoomDropProjection`.
// ───────────────────────────────────────────────────────────────────────────

export type RoomDropSlot = { overId: string; zone: RoomDropZone }

export type RoomDropSlotKey = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight'

/**
 * Every distinct insertion point in the list, top to bottom.
 *
 * `before` each row covers all of them but the tail of a run: the last child of
 * a parent (and the last row overall) also needs an `after` slot, or a child
 * could never be moved to the end of its own group. Anywhere else `after row[i]`
 * and `before row[i+1]` are the same point, and only one is listed so ↑/↓ never
 * stalls on a duplicate.
 *
 * The one extra slot is the TAIL OF THE LIST when the list ends inside a child
 * run. `after` the last child is a slot inside that parent, and every other slot
 * sits above it, so "top level, last" would have no slot at all — which is what
 * made a keyboard drag of the bottom room dead whenever the row above it was a
 * child row (`findCurrentRoomDropSlot` found nothing, and all four arrows then
 * returned null). It is appended AFTER the run's own slots, so ↑/↓ order still
 * matches visual order, and `getRoomDropProjection`'s depth-0 snap resolves it
 * past the run.
 *
 * Pass the rows WITHOUT the dragged room — slots adjacent to it are its current
 * position, not moves.
 */
export function buildRoomDropSlots(rows: readonly FlatRoomNode[]): RoomDropSlot[] {
  const slots: RoomDropSlot[] = []
  rows.forEach((row, index) => {
    slots.push({ overId: row.id, zone: 'before' })
    const next = rows[index + 1]
    if (!next || next.depth < row.depth) slots.push({ overId: row.id, zone: 'after' })
  })
  if (rows[rows.length - 1]?.depth === 1) {
    const lastTopLevel = [...rows].reverse().find((row) => row.depth === 0)
    if (lastTopLevel) slots.push({ overId: lastTopLevel.id, zone: 'after' })
  }
  return slots
}

/** Index of `slot` in `slots`; an `inside` slot maps to the position it appends at. */
function findRoomDropSlotIndex(slots: readonly RoomDropSlot[], rows: readonly FlatRoomNode[], slot: RoomDropSlot) {
  if (slot.zone !== 'inside') {
    return slots.findIndex((entry) => entry.overId === slot.overId && entry.zone === slot.zone)
  }
  const lastChild = [...rows].reverse().find((row) => row.parentId === slot.overId)
  const anchorId = lastChild?.id ?? slot.overId
  const index = slots.findIndex((entry) => entry.overId === anchorId && entry.zone === 'after')
  return index >= 0 ? index : slots.findIndex((entry) => entry.overId === slot.overId)
}

/**
 * The slot the dragged room already occupies — where a keyboard drag starts, so
 * the first ↓ advances by exactly one position.
 *
 * Found by projecting each slot rather than by index arithmetic: the slot snap
 * means a position is defined by its `(parentId, targetIndex)`, not by which row
 * it happens to sit next to.
 *
 * `null` here is a dead keyboard drag — the sentinel the arrow keys fall back to
 * belongs to no row, so every key returns null — which is why the ONLY-CHILD case
 * has an explicit fallback: once the room leaves the list its parent's run is
 * empty, no `before`/`after` slot describes "inside that parent", and only the
 * nest slot does.
 */
export function findCurrentRoomDropSlot(items: readonly FlatRoomNode[], activeId: string): RoomDropSlot | null {
  const active = items.find((item) => item.id === activeId)
  if (!active) return null
  const rows = items.filter((item) => item.id !== activeId)
  let currentIndex = 0
  for (const item of items) {
    if (item.id === activeId) break
    if (!item.isTemp && item.parentId === active.parentId) currentIndex++
  }
  const isCurrentPosition = (slot: RoomDropSlot) => {
    const projection = getRoomDropProjection({ items, activeId, overId: slot.overId, zone: slot.zone })
    return !!projection && projection.parentId === active.parentId && projection.targetIndex === currentIndex
  }
  const slot = buildRoomDropSlots(rows).find(isCurrentPosition)
  if (slot) return slot
  if (!active.parentId) return null
  const insideParent: RoomDropSlot = { overId: active.parentId, zone: 'inside' }
  return isCurrentPosition(insideParent) ? insideParent : null
}

/**
 * The slot one arrow key away, or `null` when the key does nothing here.
 *
 * →: the room immediately above the slot, or — when that is a child row — the
 * parent whose run the slot sits in, so → always reads as "put it inside the
 * room this line is under".
 * ←: back to the top level, immediately after the room it was inside. That is
 * the same position visually, one level out.
 */
export function getNextRoomDropSlot({
  items,
  activeId,
  slot,
  key,
}: {
  items: readonly FlatRoomNode[]
  activeId: string
  slot: RoomDropSlot
  key: RoomDropSlotKey
}): RoomDropSlot | null {
  const rows = items.filter((item) => item.id !== activeId)
  const rowsById = new Map(rows.map((row) => [row.id, row]))
  const activeItem = items.find((item) => item.id === activeId)
  if (!activeItem) return null

  // A drag starts hovering the dragged row itself; resolve that sentinel to the
  // slot the room already occupies so the first key steps by exactly one.
  const from = slot.overId === activeId ? (findCurrentRoomDropSlot(items, activeId) ?? slot) : slot

  if (key === 'ArrowRight') {
    if (from.zone === 'inside') return null
    const overIndex = rows.findIndex((row) => row.id === from.overId)
    const above = from.zone === 'after' ? rows[overIndex] : rows[overIndex - 1]
    const candidate = above?.depth === 1 ? (above.parentId ? rowsById.get(above.parentId) : undefined) : above
    if (!candidate || !canNestRoomInto({ items, activeId, overId: candidate.id })) return null
    return { overId: candidate.id, zone: 'inside' }
  }

  if (key === 'ArrowLeft') {
    if (from.zone === 'inside') return { overId: from.overId, zone: 'after' }
    if (activeItem.hasChildren) return null // already pinned to the top level
    const over = rowsById.get(from.overId)
    if (!over || over.depth !== 1 || !over.parentId) return null
    return { overId: over.parentId, zone: 'after' }
  }

  const slots = buildRoomDropSlots(rows)
  if (slots.length === 0) return null
  const index = findRoomDropSlotIndex(slots, rows, from)
  if (index < 0) return null
  const nextIndex = key === 'ArrowDown' ? index + 1 : index - 1
  if (nextIndex < 0 || nextIndex >= slots.length) return null
  return slots[nextIndex]
}

// ───────────────────────────────────────────────────────────────────────────
// What a move is called
//
// Both strings are built here rather than in the sidebar so the wording is
// testable without a render: the announcement is the only account a screen
// reader gets of a drop, and the toast is the only account anyone gets of what
// a drag actually changed.
// ───────────────────────────────────────────────────────────────────────────

/**
 * The live drop position, as a screen reader hears it: "inside X" for a nest,
 * "before X" / "after X" for a reorder, plus the level it lands at and its
 * position among its new siblings.
 *
 * `null` means the pointer is genuinely over nothing droppable — a drag that
 * ends there moves no room, which is exactly what it says.
 */
export function describeRoomDropPlacement({
  projection,
  activeId,
  labelOf,
}: {
  projection: RoomDropProjection | null
  activeId: string
  labelOf: (roomId: string) => string | undefined
}) {
  const named = (roomId: string) => labelOf(roomId) ?? 'Room'
  if (!projection) return `${named(activeId)} is not over a drop position.`
  const position = projection.targetIndex + 1
  if (projection.insideRoomId) {
    return `${named(activeId)} inside ${named(projection.insideRoomId)}, position ${position}.`
  }
  const place = projection.parentId ? `inside ${named(projection.parentId)}` : 'at the top level'
  return `${named(activeId)} ${projection.zone} ${named(projection.overId)}, ${place}, position ${position}.`
}

/**
 * What the move toast is titled — which has to be what actually changed, in the
 * words the list itself uses.
 *
 * A same-parent reorder never changes level, so saying "to the top level" (as
 * every non-nesting move once did) reads as though the room had come out of
 * something — and "to position 4" names a number that appears NOWHERE in the
 * UI. A reorder is read off its neighbours, so it is reported off its
 * neighbours, from the same `(zone, overId)` pair the drop announcement
 * ({@link describeRoomDropPlacement}) speaks in:
 *   - into a room      — the room gained a parent, or swapped one for another
 *   - out of a room    — the room lost its parent
 *   - above / below Y  — same parent, so only the order moved
 *
 * The `inside` case with an unchanged parent is a room dropped onto the room it
 * is already in: that appends it to the end of its own group, which has no
 * neighbour above or below to name.
 */
export function describeRoomMove({
  movedLabel,
  fromParentId,
  projection,
  labelOf,
}: {
  movedLabel: string
  fromParentId: string | null
  /** The drop being reported — the same projection the plan was built from. */
  projection: RoomDropProjection
  labelOf: (roomId: string) => string | undefined
}) {
  const named = (roomId: string) => labelOf(roomId) ?? 'that room'
  const toParentId = projection.parentId

  if (fromParentId !== toParentId) {
    if (toParentId) return `Moved "${movedLabel}" into "${named(toParentId)}"`
    return fromParentId
      ? `Moved "${movedLabel}" out of "${named(fromParentId)}"`
      : `Moved "${movedLabel}" to the top level`
  }

  if (projection.insideRoomId) return `Moved "${movedLabel}" to the end of "${named(projection.insideRoomId)}"`
  return `Moved "${movedLabel}" ${projection.zone === 'before' ? 'above' : 'below'} "${named(projection.overId)}"`
}

/** One `dsy_sequencenumber` write. */
export type RoomSequenceUpdate = { dsy_accountroomid: string; dsy_sequencenumber: number }

/**
 * Every write one drag produces: the moved room's parent bind + sequence, and
 * the sibling sequence patches the insertion forced. Siblings are the rows whose
 * number actually changes — nothing else is touched.
 */
export type RoomMovePlan = {
  roomId: string
  parentRoomId: string | null
  sequenceNumber: number
  targetIndex: number
  siblingUpdates: RoomSequenceUpdate[]
}

/**
 * Fused plan for a drag-to-reparent: one moved room, N sibling renumbers.
 *
 * - **Append** (`targetIndex === targetSiblings.length`): the room takes the
 *   global `max + 1` and NOTHING else is written. Re-dealing the union here
 *   would renumber siblings that never moved.
 * - **Insertion**: the union `S = targetSiblings ∪ {room}` of existing numbers is
 *   dealt along the new order, so global uniqueness holds and no room outside the
 *   target group is touched. A head insert changes every row (|T| + 1 patches).
 * - **Fallback** (a null or a duplicate in that union): the whole group is dealt
 *   `maxSequenceNumber + 1…`, which is globally unique by construction. For a
 *   top-level target that renumbers every top-level room — relative order is
 *   preserved, but the blast radius is the whole level.
 *
 * `temp-` rooms are excluded from the plan itself, not just from the writes: a
 * room with no record yet cannot hold a sequence slot.
 */
export function buildRoomMovePlan({
  room,
  targetParentId,
  targetSiblings,
  targetIndex,
  maxSequenceNumber,
}: {
  room: AccountRoomData
  targetParentId: string | null
  targetSiblings: AccountRoomData[]
  targetIndex: number
  maxSequenceNumber: number
}): RoomMovePlan {
  const siblings = targetSiblings.filter(
    (sibling) => sibling.dsy_accountroomid !== room.dsy_accountroomid && !isTempRoomId(sibling.dsy_accountroomid)
  )
  const index = Math.max(0, Math.min(targetIndex, siblings.length))

  if (index === siblings.length) {
    return {
      roomId: room.dsy_accountroomid,
      parentRoomId: targetParentId,
      sequenceNumber: maxSequenceNumber + 1,
      targetIndex: index,
      siblingUpdates: [],
    }
  }

  const order = [...siblings.slice(0, index), room, ...siblings.slice(index)]
  const existing = order.map((entry) => entry.dsy_sequencenumber)
  const usable =
    existing.every((seq): seq is number => typeof seq === 'number') && new Set(existing).size === order.length
  const slots = usable
    ? [...(existing as number[])].sort((a, b) => a - b)
    : order.map((_, position) => maxSequenceNumber + position + 1)

  return {
    roomId: room.dsy_accountroomid,
    parentRoomId: targetParentId,
    sequenceNumber: slots[index],
    targetIndex: index,
    siblingUpdates: order
      .map((entry, position) => ({
        dsy_accountroomid: entry.dsy_accountroomid,
        dsy_sequencenumber: slots[position],
      }))
      .filter(
        (update, position) =>
          position !== index && update.dsy_sequencenumber !== order[position].dsy_sequencenumber
      ),
  }
}

/**
 * The plan as ONE atomic Dataverse changeset: the moved room's parent bind and
 * sequence in a single PATCH, then a PATCH per displaced sibling.
 *
 * Every write in a changeset commits or rolls back together, so the plan needs no
 * staging: the transient duplicate `dsy_sequencenumber` that a re-deal passes
 * through has no observable state to be observed in, and a failure leaves the
 * account exactly as it was. (The sequential fan-out this replaced parked the
 * moved room above the max first; a reload mid-chain persisted the parking value
 * and the room silently landed last in its group on every quote of the account.)
 *
 * `etag: '*'` is `updateRecord`'s `If-Match: *`, kept deliberately: without an
 * If-Match header Dataverse treats a PATCH as an upsert, so a room deleted since
 * the rooms query would be RESURRECTED as a nameless record — which the rooms
 * schema then refuses to parse. `upsert: true` is the opposite header
 * (`If-None-Match: *`, create-only) and would fail every one of these.
 *
 * Order is the plan's own: moved room first, siblings in group order. Nothing
 * depends on it — the changeset is one transaction — it just reads like the plan.
 */
export function buildRoomMoveOperations(plan: RoomMovePlan): DataverseChangesetOperation[] {
  return [
    {
      method: 'PATCH',
      entitySet: 'dsy_accountrooms',
      id: plan.roomId,
      etag: '*',
      body: {
        'dsy_parentroom@odata.bind': plan.parentRoomId ? `/dsy_accountrooms(${plan.parentRoomId})` : null,
        dsy_sequencenumber: plan.sequenceNumber,
      },
    },
    ...plan.siblingUpdates.map((update) => ({
      method: 'PATCH' as const,
      entitySet: 'dsy_accountrooms' as const,
      id: update.dsy_accountroomid,
      etag: '*',
      body: { dsy_sequencenumber: update.dsy_sequencenumber },
    })),
  ]
}

/** The plan applied to a room list — the optimistic write, and the test oracle. */
export function applyRoomMovePlan(rooms: AccountRoomData[], plan: RoomMovePlan): AccountRoomData[] {
  const sequenceByRoomId = new Map(
    plan.siblingUpdates.map((update) => [update.dsy_accountroomid, update.dsy_sequencenumber])
  )
  return rooms.map((room) => {
    if (room.dsy_accountroomid === plan.roomId) {
      return { ...room, _dsy_parentroom_value: plan.parentRoomId, dsy_sequencenumber: plan.sequenceNumber }
    }
    const sequenceNumber = sequenceByRoomId.get(room.dsy_accountroomid)
    return sequenceNumber === undefined ? room : { ...room, dsy_sequencenumber: sequenceNumber }
  })
}

/**
 * The literal inverse of an executed plan: every row it wrote, restored to the
 * value it held in `roomsBefore`. Never re-planned — re-planning a move back to
 * the top level would hand the room a value dealt for a child group and walk
 * global uniqueness off a cliff.
 *
 * Returns `null` when a written row had no sequence number to restore (imported
 * data), because the write shape cannot express "back to null" — the caller then
 * offers no Undo rather than a lossy one.
 */
export function buildRoomMoveUndoPlan(plan: RoomMovePlan, roomsBefore: AccountRoomData[]): RoomMovePlan | null {
  const roomsById = new Map(roomsBefore.map((room) => [room.dsy_accountroomid, room]))
  const moved = roomsById.get(plan.roomId)
  if (!moved || typeof moved.dsy_sequencenumber !== 'number') return null

  const siblingUpdates: RoomSequenceUpdate[] = []
  for (const update of plan.siblingUpdates) {
    const before = roomsById.get(update.dsy_accountroomid)
    if (!before || typeof before.dsy_sequencenumber !== 'number') return null
    siblingUpdates.push({
      dsy_accountroomid: update.dsy_accountroomid,
      dsy_sequencenumber: before.dsy_sequencenumber,
    })
  }

  return {
    roomId: plan.roomId,
    parentRoomId: moved._dsy_parentroom_value,
    sequenceNumber: moved.dsy_sequencenumber,
    targetIndex: plan.targetIndex,
    siblingUpdates,
  }
}

/**
 * Whether every row the plan wrote still holds the value it wrote. Undo runs
 * only while this holds: anything else (a concurrent reorder, another user's
 * move) and the inverse patch set would overwrite work it never saw.
 */
export function isRoomMovePlanApplied(rooms: AccountRoomData[], plan: RoomMovePlan) {
  const roomsById = new Map(rooms.map((room) => [room.dsy_accountroomid, room]))
  const moved = roomsById.get(plan.roomId)
  if (!moved) return false
  if ((moved._dsy_parentroom_value ?? null) !== plan.parentRoomId) return false
  if (moved.dsy_sequencenumber !== plan.sequenceNumber) return false
  return plan.siblingUpdates.every(
    (update) => roomsById.get(update.dsy_accountroomid)?.dsy_sequencenumber === update.dsy_sequencenumber
  )
}
